// api.js

import { logAction } from './logging';

// Basis-URL: in Docker/Produktion => '/api'
// lokal (npm start) => aus .env.development: http://localhost:8080/api
const API_ROOT = process.env.REACT_APP_API_BASE || '/api';

// --- Navigation settings --------------------------------------------------

export async function getNavigationSettings() {
  const res = await fetch(`${API_ROOT}/settings/navigation`);
  if (!res.ok) {
    throw new Error('Navigationseinstellungen konnten nicht geladen werden.');
  }
  return res.json();
}

export async function updateNavigationSettings(token, payload) {
  const res = await authFetch('/settings/navigation', token, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error('Navigationseinstellungen konnten nicht gespeichert werden.');
  }
  return res.json();
}

// --- Inventory -------------------------------------------------------

export async function listStockLevels(token, params = {}) {
  const searchParams = new URLSearchParams();
  if (params.search) {
    searchParams.set("search", params.search);
  }
  if (params.locationId) {
    searchParams.set("locationId", params.locationId);
  }
  if (params.sort) {
    searchParams.set("sort", params.sort);
  }
  if (params.direction) {
    searchParams.set("direction", params.direction);
  }
  const query = searchParams.toString();
  const res = await authFetch(`/inventory/stock-levels${query ? `?${query}` : ""}`, token);
  if (!res.ok) {
    throw await parseProblemDetail(res, "Bestände konnten nicht geladen werden.");
  }
  return res.json();
}

export async function getStockLevel(token, stockLevelId) {
  const res = await authFetch(`/inventory/stock-levels/${stockLevelId}`, token);
  if (!res.ok) {
    throw await parseProblemDetail(res, "Bestand konnte nicht geladen werden.");
  }
  return res.json();
}

export async function getStockJournal(token, stockLevelId, params = {}) {
  const searchParams = new URLSearchParams();
  if (params.from) {
    searchParams.set("from", params.from);
  }
  if (params.to) {
    searchParams.set("to", params.to);
  }
  if (params.type) {
    searchParams.set("type", params.type);
  }
  if (params.search) {
    searchParams.set("search", params.search);
  }
  const query = searchParams.toString();
  const res = await authFetch(
    `/inventory/stock-levels/${stockLevelId}/journal${query ? `?${query}` : ""}`,
    token
  );
  if (!res.ok) {
    throw await parseProblemDetail(res, "Bewegungsjournal konnte nicht geladen werden.");
  }
  return res.json();
}

// --- Customers -------------------------------------------------------

export async function searchCustomers(token, params = {}) {
  const searchParams = new URLSearchParams();
  if (params.query) {
    searchParams.set('query', params.query);
  }
  if (params.page != null) {
    searchParams.set('page', params.page);
  }
  if (params.size != null) {
    searchParams.set('size', params.size);
  }
  const query = searchParams.toString();
  const res = await authFetch(`/customers${query ? `?${query}` : ''}`, token);
  if (!res.ok) throw await parseProblemDetail(res, 'Kunden konnten nicht geladen werden.');
  return res.json();
}

export async function getCustomer(token, id) {
  const res = await authFetch(`/customers/${id}`, token);
  if (!res.ok) throw await parseProblemDetail(res, 'Kunde konnte nicht geladen werden.');
  return res.json();
}

export async function getCustomerDetail(token, id) {
  const res = await authFetch(`/customers/${id}/detail`, token);
  if (!res.ok) throw await parseProblemDetail(res, 'Kunde konnte nicht geladen werden.');
  return res.json();
}

export async function createCustomer(token, payload) {
  const res = await authFetch('/customers', token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await parseProblemDetail(res, 'Kunde konnte nicht erstellt werden.');
  return res.json();
}

export async function updateCustomer(token, id, payload) {
  const res = await authFetch(`/customers/${id}`, token, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await parseProblemDetail(res, 'Kunde konnte nicht aktualisiert werden.');
  return res.json();
}

// --- Vehicles -------------------------------------------------------

export async function searchVehicles(token, params = {}) {
  const searchParams = new URLSearchParams();
  if (params.query) {
    searchParams.set('query', params.query);
  }
  if (params.customerId) {
    searchParams.set('customerId', params.customerId);
  }
  const query = searchParams.toString();
  const res = await authFetch(`/vehicles${query ? `?${query}` : ''}`, token);
  if (!res.ok) throw await parseProblemDetail(res, 'Fahrzeuge konnten nicht geladen werden.');
  return res.json();
}

export async function getVehicle(token, id) {
  const res = await authFetch(`/vehicles/${id}`, token);
  if (!res.ok) throw await parseProblemDetail(res, 'Fahrzeug konnte nicht geladen werden.');
  return res.json();
}

export async function updateVehicle(token, id, payload) {
  const res = await authFetch(`/vehicles/${id}`, token, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await parseProblemDetail(res, 'Fahrzeug konnte nicht aktualisiert werden.');
  return res.json();
}

export async function enrichVehicle(token, id, payload = {}) {
  const vin = typeof payload.vin === 'string' ? payload.vin.trim().toUpperCase() : '';
  const hsn = typeof payload.hsn === 'string' ? payload.hsn.trim().toUpperCase() : '';
  const tsn = typeof payload.tsn === 'string' ? payload.tsn.trim().toUpperCase() : '';
  const consent = payload.consent === undefined ? true : Boolean(payload.consent);

  let path = null;
  let body = null;

  if (vin) {
    path = `/vehicles/${id}/enrich/vin`;
    body = { vin, consent };
    if (hsn) {
      body.hsn = hsn;
    }
    if (tsn) {
      body.tsn = tsn;
    }
  } else if (hsn && tsn) {
    path = `/vehicles/${id}/enrich/hsn-tsn`;
    body = { hsn, tsn, consent };
  } else {
    throw new Error('VIN or HSN/TSN required for vehicle enrichment.');
  }

  const res = await authFetch(path, token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await parseProblemDetail(res, 'Fahrzeug konnte nicht angereichert werden.');
  return res.json();
}

export async function exportVehicle(token, id) {
  const res = await authFetch(`/vehicles/${id}/export`, token);
  if (!res.ok) throw await parseProblemDetail(res, 'Fahrzeugdaten konnten nicht exportiert werden.');
  return res.json();
}

// --- Work Orders -------------------------------------------------------

export async function listWorkOrders(token, params = {}) {
  const searchParams = new URLSearchParams();
  if (params.status) {
    searchParams.set('status', params.status);
  }
  if (params.from) {
    searchParams.set('from', params.from);
  }
  if (params.to) {
    searchParams.set('to', params.to);
  }
  const query = searchParams.toString();
  const res = await authFetch(`/work-orders${query ? `?${query}` : ''}`, token);
  if (!res.ok) throw new Error('request failed');
  return res.json();
}

export async function createWorkOrder(token, payload) {
  const res = await authFetch('/work-orders', token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('request failed');
  return res.json();
}

export async function updateWorkOrder(token, id, payload) {
  const res = await authFetch(`/work-orders/${id}`, token, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('request failed');
  return res.json();
}

export async function deleteWorkOrder(token, id) {
  const res = await authFetch(`/work-orders/${id}`, token, { method: 'DELETE' });
  if (!res.ok) throw new Error('request failed');
  return res;
}

export async function listWorkOrderOperations(token, workOrderId) {
  const res = await authFetch(`/work-orders/${workOrderId}/operations`, token);
  if (!res.ok) throw new Error('request failed');
  return res.json();
}

export async function createWorkOrderOperation(token, workOrderId, payload) {
  const res = await authFetch(`/work-orders/${workOrderId}/operations`, token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('request failed');
  return res.json();
}

export async function updateWorkOrderOperation(token, workOrderId, operationId, payload) {
  const res = await authFetch(`/work-orders/${workOrderId}/operations/${operationId}`, token, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('request failed');
  return res.json();
}

export async function deleteWorkOrderOperation(token, workOrderId, operationId) {
  const res = await authFetch(`/work-orders/${workOrderId}/operations/${operationId}`, token, { method: 'DELETE' });
  if (!res.ok) throw new Error('request failed');
  return res;
}

export async function startTimeLog(token, payload) {
  const res = await authFetch('/time-logs/start', token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const error = new Error('request failed');
    error.status = res.status;
    throw error;
  }
  return res.json();
}

export async function stopTimeLog(token, payload) {
  const res = await authFetch('/time-logs/stop', token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const error = new Error('request failed');
    error.status = res.status;
    throw error;
  }
  return res.json();
}

export async function listAppointments(token) {
  const res = await authFetch('/appointment/all', token);
  if (!res.ok) throw new Error('request failed');
  return res.json();
}

export async function getTermine() {
  const res = await fetch(`${API_ROOT}/termine`);
  return res.json();
}

export async function createTermin(payload) {
  const res = await fetch(`${API_ROOT}/termine`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return res.json();
}

// --- Announcements -----------------------------------------------------

export async function getAnnouncements(params = {}) {
  const query = new URLSearchParams(params).toString();
  const res = await fetch(`${API_ROOT}/announcements${query ? `?${query}` : ''}`);
  if (!res.ok) throw new Error('request failed');
  return res.json();
}

export async function createAnnouncement(token, payload) {
  const res = await authFetch('/announcements', token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('request failed');
  return res.json();
}

export async function updateAnnouncement(token, id, payload) {
  const res = await authFetch(`/announcements/${id}`, token, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('request failed');
  return res.json();
}

// --- Opening Times -------------------------------------------------------

export async function getOpeningTimes() {
  const res = await fetch(`${API_ROOT}/opening-times`);
  if (!res.ok) throw new Error('request failed');
  return res.json();
}

export async function createOpeningTime(token, payload) {
  const res = await authFetch('/opening-times', token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('request failed');
  return res.json();
}

export async function updateOpeningTime(token, id, payload) {
  const res = await authFetch(`/opening-times/${id}`, token, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('request failed');
  return res.json();
}

// --- Vacations -------------------------------------------------------

export async function getVacations() {
  const res = await fetch(`${API_ROOT}/vacations`);
  if (!res.ok) throw new Error('request failed');
  return res.json();
}

export async function createVacation(token, payload) {
  const res = await authFetch('/vacations', token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('request failed');
  return res.json();
}

export async function deleteVacation(token, id) {
  const res = await authFetch(`/vacations/${id}`, token, { method: 'DELETE' });
  if (!res.ok) throw new Error('request failed');
  return res;
}


export async function submitFeedback(token, payload) {
  const res = await authFetch('/feedback', token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('request failed');
  return res;
}

// --- DevOps Monitoring -------------------------------------------------------

export async function getTecDocMonitoring(token) {
  const res = await authFetch('/devops/monitoring/tecdoc', token);
  if (!res.ok) {
    throw await parseProblemDetail(res, 'Monitoring konnte nicht geladen werden.');
  }
  return res.json();
}

// --- Auth & Admin -------------------------------------------------------

async function parseProblemDetail(res, fallbackMessage) {
  const contentType = res.headers.get('content-type') || '';
  const text = await res.text();
  let data = null;
  if (text) {
    if (contentType.includes('application/json')) {
      try {
        data = JSON.parse(text);
      } catch (err) {
        data = null;
      }
    } else {
      try {
        data = JSON.parse(text);
      } catch (err) {
        data = { detail: text };
      }
    }
  }
  const error = new Error((data && data.detail) || fallbackMessage || 'request failed');
  error.status = res.status;
  if (data?.title) {
    error.title = data.title;
  }
  if (Array.isArray(data?.errors)) {
    error.validationErrors = data.errors;
  }
  if (data) {
    error.body = data;
  }
  return error;
}

async function authFetch(path, token, options = {}) {
  const res = await fetch(`${API_ROOT}${path}`, {
    ...options,
    headers: {
      ...(options.headers || {}),
      Authorization: `Bearer ${token}`,
    },
  });
  if (res.status === 401) {
    throw new Error('unauthorized');
  }
  return res;
}

export async function loginUser(username, password) {
  const res = await fetch(`${API_ROOT}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  if (!res.ok) throw new Error('Login fehlgeschlagen');
  return res.json();
}

export async function getAdminUsers(token) {
  const res = await authFetch('/admin/users', token);
  if (!res.ok) throw new Error('request failed');
  return res.json();
}

export async function listMechanics(token) {
  const res = await authFetch('/users/mechanics', token);
  if (!res.ok) throw new Error('request failed');
  return res.json();
}

export async function createAdminUser(token, payload) {
  const res = await authFetch('/admin/users', token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('request failed');
  return res.json();
}

export async function updateAdminUser(token, id, payload) {
  const res = await authFetch(`/admin/users/${id}`, token, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('request failed');
  return res.json();
}

// --- Structured log dashboard -------------------------------------------------

function appendQueryParam(searchParams, key, value) {
  if (value === undefined || value === null || value === '') {
    return;
  }
  searchParams.set(key, value);
}

export async function searchStructuredLogs(token, params = {}) {
  const searchParams = new URLSearchParams();
  appendQueryParam(searchParams, 'correlationId', params.correlationId);
  appendQueryParam(searchParams, 'event', params.event);
  if (Array.isArray(params.events)) {
    params.events.filter(Boolean).forEach((value) => searchParams.append('events', value));
  }
  appendQueryParam(searchParams, 'level', params.level);
  appendQueryParam(searchParams, 'status', params.status);
  appendQueryParam(searchParams, 'from', params.from);
  appendQueryParam(searchParams, 'to', params.to);
  if (params.page != null) {
    searchParams.set('page', params.page);
  }
  if (params.size != null) {
    searchParams.set('size', params.size);
  }
  const query = searchParams.toString();
  const res = await authFetch(`/admin/logs${query ? `?${query}` : ''}`, token);
  if (!res.ok) {
    throw await parseProblemDetail(res, 'Logs konnten nicht geladen werden.');
  }
  return res.json();
}

export async function getLogTimeline(token, correlationId) {
  if (!correlationId) {
    throw new Error('correlationId required');
  }
  const res = await authFetch(`/admin/logs/correlation/${encodeURIComponent(correlationId)}`, token);
  if (!res.ok) {
    throw await parseProblemDetail(res, 'Timeline konnte nicht geladen werden.');
  }
  return res.json();
}

export async function getEndpointAggregations(token, params = {}) {
  const searchParams = new URLSearchParams();
  appendQueryParam(searchParams, 'from', params.from);
  appendQueryParam(searchParams, 'to', params.to);
  appendQueryParam(searchParams, 'method', params.method);
  const query = searchParams.toString();
  const res = await authFetch(`/admin/logs/aggregations/endpoints${query ? `?${query}` : ''}`, token);
  if (!res.ok) {
    throw await parseProblemDetail(res, 'Aggregationen konnten nicht geladen werden.');
  }
  return res.json();
}

export async function getCorrelationAggregations(token, params = {}) {
  const searchParams = new URLSearchParams();
  appendQueryParam(searchParams, 'from', params.from);
  appendQueryParam(searchParams, 'to', params.to);
  const query = searchParams.toString();
  const res = await authFetch(`/admin/logs/aggregations/correlations${query ? `?${query}` : ''}`, token);
  if (!res.ok) {
    throw await parseProblemDetail(res, 'Korrelationsmetriken konnten nicht geladen werden.');
  }
  return res.json();
}

export async function getLogAlerts(token, params = {}) {
  const searchParams = new URLSearchParams();
  appendQueryParam(searchParams, 'from', params.from);
  appendQueryParam(searchParams, 'to', params.to);
  const query = searchParams.toString();
  const res = await authFetch(`/admin/logs/alerts${query ? `?${query}` : ''}`, token);
  if (!res.ok) {
    throw await parseProblemDetail(res, 'Alerts konnten nicht geladen werden.');
  }
  return res.json();
}

export async function deleteAdminUser(token, id) {
  const res = await authFetch(`/admin/users/${id}`, token, { method: 'DELETE' });
  if (!res.ok) throw new Error('request failed');
  return res;
}

// --- Supply Chain API ------------------------------------------------------

export async function decodeVehicleByVin(token, vin) {
  const res = await authFetch('/catalog/vehicle/vin', token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ vin }),
  });
  return res.json();
}

export async function fetchVehicleParts(token, vehicleId, params = {}) {
  const query = new URLSearchParams(params).toString();
  const res = await authFetch(`/catalog/vehicle/${vehicleId}/parts${query ? `?${query}` : ''}`, token);
  return res.json();
}

export async function submitPricingInquiry(token, payload) {
  const res = await authFetch('/pricing/inquiry', token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return res.json();
}

export async function createBasket(token, payload) {
  const res = await authFetch('/basket', token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return res.json();
}

export async function addBasketLine(token, basketId, payload) {
  const res = await authFetch(`/basket/${basketId}/lines`, token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return res.json();
}

export async function checkoutBasket(token, basketId, payload) {
  const res = await authFetch(`/checkout/${basketId}`, token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return res.json();
}

export async function getOrders(token, params = {}) {
  const query = new URLSearchParams(params).toString();
  const res = await authFetch(`/orders${query ? `?${query}` : ''}`, token);
  return res.json();
}

export async function getOrderStatus(token, orderId) {
  const res = await authFetch(`/orders/${orderId}/status`, token);
  return res.json();
}

export async function postGoodsReceipt(token, orderId, payload) {
  const res = await authFetch(`/goods-receipt/${orderId}`, token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return res.json();
}

export async function importInvoice(token, payload) {
  const res = await authFetch('/invoices/import', token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return res.json();
}

export async function getReconciliation(token, orderId) {
  const res = await authFetch(`/reconciliation/${orderId}`, token);
  return res.json();
}

export async function getInvoiceForWorkOrder(token, workOrderId) {
  const res = await authFetch(`/invoices/work-order/${workOrderId}`, token);
  if (res.status === 404) {
    return null;
  }
  if (!res.ok) {
    const error = new Error('request failed');
    error.status = res.status;
    throw error;
  }
  return res.json();
}

export async function createInvoice(token, payload) {
  const res = await authFetch('/invoices', token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const error = new Error('request failed');
    error.status = res.status;
    throw error;
  }
  return res.json();
}

export async function updateInvoice(token, invoiceId, payload) {
  const res = await authFetch(`/invoices/${invoiceId}`, token, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const error = new Error('request failed');
    error.status = res.status;
    throw error;
  }
  return res.json();
}

export async function downloadInvoicePdf(token, invoiceId) {
  const res = await authFetch(`/invoices/${invoiceId}/pdf`, token);
  if (!res.ok) {
    const error = new Error('request failed');
    error.status = res.status;
    throw error;
  }
  const blob = await res.blob();
  let fileName = `invoice-${invoiceId}.pdf`;
  const disposition = res.headers.get('Content-Disposition');
  if (disposition) {
    const encodedMatch = disposition.match(/filename\*=UTF-8''([^;]+)/);
    if (encodedMatch && encodedMatch[1]) {
      try {
        fileName = decodeURIComponent(encodedMatch[1]);
      } catch (error) {
        fileName = encodedMatch[1];
      }
    } else {
      const simpleMatch = disposition.match(/filename="?([^";]+)"?/);
      if (simpleMatch && simpleMatch[1]) {
        fileName = simpleMatch[1];
      }
    }
  }
  return { blob, fileName };
}

// --- Car Offers -------------------------------------------------------

export async function getCars() {
  const res = await fetch(`${API_ROOT}/cars`);
  if (!res.ok) throw new Error('request failed');
  return res.json();
}

export async function createCar(token, payload) {
  const res = await authFetch('/cars', token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('request failed');
  return res.json();
}

export async function updateCar(token, id, payload) {
  const res = await authFetch(`/cars/${id}`, token, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('request failed');
  return res.json();
}

export async function deleteCar(token, id) {
  const res = await authFetch(`/cars/${id}`, token, { method: 'DELETE' });
  if (!res.ok) throw new Error('request failed');
  return res;
}

// --- Services -------------------------------------------------------

export async function getServices() {
  const res = await fetch(`${API_ROOT}/services`);
  if (!res.ok) throw new Error('request failed');
  return res.json();
}

export async function createService(token, payload) {
  const res = await authFetch('/services', token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('request failed');
  return res.json();
}

export async function updateService(token, id, payload) {
  const res = await authFetch(`/services/${id}`, token, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('request failed');
  return res.json();
}

export async function deleteService(token, id) {
  const res = await authFetch(`/services/${id}`, token, { method: 'DELETE' });
  if (!res.ok) throw new Error('request failed');
  return res;
}

// --- Appointments -------------------------------------------------------

export async function getAppointmentSlots(date) {
  const res = await fetch(`${API_ROOT}/appointment/slots?date=${date}`);
  if (!res.ok) throw new Error('request failed');
  return res.json();
}

export async function bookAppointment(payload) {
  logAction('Attempt to book appointment');
  const res = await fetch(`${API_ROOT}/appointment/book`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const status = res.status;
    logAction(`Book appointment failed (status ${status})`, 'ERROR');

    let backendMessage = '';
    try {
      const errorText = await res.text();
      if (errorText) {
        try {
          const parsed = JSON.parse(errorText);
          if (parsed && typeof parsed.message === 'string') {
            backendMessage = parsed.message;
          } else if (typeof parsed === 'string') {
            backendMessage = parsed;
          }
        } catch (parseErr) {
          backendMessage = errorText;
        }
      }
    } catch (readErr) {
      // ignore body parsing errors and fall back to default message
    }

    if (status === 409) {
      const conflictError = new Error('Slot bereits vergeben!');
      conflictError.status = status;
      conflictError.backendMessage = backendMessage;
      throw conflictError;
    }

    const message = backendMessage || 'Termin konnte nicht gebucht werden.';
    const error = new Error(message);
    error.status = status;
    error.backendMessage = backendMessage;
    throw error;
  }
  logAction('Book appointment success');
  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return res.json();
  }
  return res.text();
}

export async function findCustomerSuggestions(payload = {}) {
  const res = await fetch(`${API_ROOT}/appointment/customer-suggestions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('request failed');
  return res.json();
}

export async function findVehicleSuggestions(payload = {}, options = {}) {
  const requestOptions = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  };
  if (options && options.signal) {
    requestOptions.signal = options.signal;
  }
  const res = await fetch(`${API_ROOT}/appointment/vehicle-suggestions`, requestOptions);
  if (!res.ok) throw new Error('request failed');
  return res.json();
}

export async function createAppointment(token, payload) {
  const res = await authFetch('/appointment/create', token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('request failed');
  return res.json();
}

export async function getAppointments(token) {
  const res = await authFetch('/appointment/all', token);
  if (!res.ok) throw new Error('request failed');
  return res.json();
}

export async function confirmAppointment(token, id) {
  const res = await authFetch(`/appointment/${id}/confirm`, token, { method: 'PATCH' });
  if (!res.ok) throw new Error('request failed');
  return res;
}

export async function cancelAppointment(token, id) {
  const res = await authFetch(`/appointment/${id}/cancel`, token, { method: 'PATCH' });
  if (!res.ok) throw new Error('request failed');
  return res;
}

export async function rescheduleAppointment(token, id, payload) {
  return authFetch(`/appointment/${id}/reschedule`, token, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}
